import React from 'react';
import { Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { SignIn } from '@features/sign-in';
import { Sales } from '@features/sales';
import { ProductCatalog, ProductDetails } from '@features/products';
import { SignInValidate, SignInPassword, CompanySelect } from '@features/sign-in';
import CompanySelectionGate from '@features/sign-in/components/CompanySelectionGate';
import { SignUpFlow, ShopifyInstallSignUp, ReferralLanding, ShopifyInstallOnboarding, OnboardingFlow } from '@features/sign-up';
import MainScreen from '@features/MainScreen';
import { DashboardHome } from '@features/dashboard';
import { DashboardOnboarding } from '@features/tutorial';
import DashboardLandingRedirect from '@features/dashboard/DashboardLandingRedirect';
import { InventoryCatalog } from '@features/inventory';
import { PurchasingLayout } from '@features/purchasing';
import { Suppliers, SupplierDetail } from '@features/suppliers';
import { Company } from '@features/company';
import { SubscriptionSuccess, SubscriptionCancel, SubscriptionDetailsPage } from '@features/subscription';
import BolCallback from './features/connections/bol/components/BolCallBack';
import AmazonCallback from './features/connections/amazon/components/AmazonCallback';
import AmazonLogin from './features/connections/amazon/components/AmazonLogin';
import { WarehouseDetail, WarehouseInventoryMutations } from '@features/warehouses';
import { TimeTravelProduct, TimeTravelSearch } from '@features/timetravel';
import TimeTravelRank from '@features/timetravel/TimeTravelRank';
import ConnectionSetup from '@features/connections/generic/components/ConnectionSetup';

const TimeTravelSalesRedirect: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  return <Navigate to={`/products/${productId}?tab=sales-timetravel`} replace />;
};

const AuthenticatedRouter = () => {
  return (
    <CompanySelectionGate>
    <Routes>
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-in/validate" element={<SignInValidate />} />
      <Route path="/sign-in/password" element={<SignInPassword />} />
      <Route path="/sign-in/select-company" element={<CompanySelect />} />
      <Route path="/referral/:code" element={<ReferralLanding />} />
      <Route path="/sign-up/shopify" element={<ShopifyInstallSignUp />} />
      <Route path="/sign-up/*" element={<SignUpFlow />} />
      <Route path="/onboarding/shopify" element={<ShopifyInstallOnboarding />} />
      <Route path="/onboarding/*" element={<OnboardingFlow />} />
      <Route path="/company/connections/:id/setup" element={<ConnectionSetup />} />

      <Route path="/callback/marketplace-connection/bol" element={<BolCallback />} />
      <Route path="/callback/marketplace-connection/amazon/login" element={<AmazonLogin />} />
      <Route path="/callback/marketplace-connection/amazon" element={<AmazonCallback />} />

      <Route path="/subscription/success" element={<SubscriptionSuccess />} />
      <Route path="/subscription/cancel" element={<SubscriptionCancel />} />
      <Route path="/subscription/details" element={<SubscriptionDetailsPage />} />

      <Route element={<MainScreen />}>
        <Route path="/dashboard" element={<DashboardHome />} />
        <Route path="/dashboard/onboarding" element={<DashboardOnboarding />} />
        <Route path="/sales" element={<Sales />} />
        <Route path="product-catalog" element={<Outlet />}>
          <Route index element={<Navigate to="/product-catalog/products" replace />} />
          <Route path="products" element={<ProductCatalog />} />

          <Route path="collections" element={<ProductCatalog />} />
        </Route>
        <Route path="products/:productId" element={<ProductDetails />} />
        <Route path="/inventory" element={<InventoryCatalog />} />
        <Route path="/inventory/warehouses" element={<InventoryCatalog />} />
        <Route path="/inventory/warehouses/:id" element={<WarehouseDetail />} />
        <Route path="/inventory/warehouses/:id/mutations" element={<WarehouseInventoryMutations />} />
        <Route path="/purchasing" element={<PurchasingLayout />} />
        <Route path="/purchasing/shipments" element={<PurchasingLayout />} />
        <Route path="/suppliers" element={<Suppliers />} />
        <Route path="/suppliers/:id" element={<SupplierDetail />} />
        <Route path="/company" element={<Company />} />

        <Route path="/timetravel/sales/:productId" element={<TimeTravelSalesRedirect />} />
        <Route path="/timetravel/search" element={<TimeTravelSearch />} />
        <Route path="/timetravel/search/:searchTerm" element={<TimeTravelSearch />} />
        <Route path="/timetravel/ranks/:rankId" element={<TimeTravelRank />} />
        <Route path="/timetravel/products" element={<TimeTravelProduct />} />
        <Route path="/timetravel/products/:productId" element={<TimeTravelProduct />} />
      </Route>

      <Route path="*" element={<DashboardLandingRedirect />} />
    </Routes>
    </CompanySelectionGate>
  );
};

const Router = () => {
  return <AuthenticatedRouter />;
};

export default Router;
